import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, FileText, FileSpreadsheet, Building2 } from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";

const fmtCRC = (n) => `₡${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtUSD = (n) => `$${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS = [
  ["1", "Enero"], ["2", "Febrero"], ["3", "Marzo"], ["4", "Abril"],
  ["5", "Mayo"], ["6", "Junio"], ["7", "Julio"], ["8", "Agosto"],
  ["9", "Setiembre"], ["10", "Octubre"], ["11", "Noviembre"], ["12", "Diciembre"],
];

const QUARTERS = [
  ["1", "Q1 (Ene-Mar)"], ["2", "Q2 (Abr-Jun)"], ["3", "Q3 (Jul-Sep)"], ["4", "Q4 (Oct-Dic)"],
];

const CAT_LABELS = {
  ventas_efectivo: "Ventas Efectivo",
  sinpe: "SINPE Móvil",
  bancos: "Pagos por Banco",
  premios: "Premios (Salida)",
  entradas: "Entradas",
  salidas: "Salidas",
  ventas_inventario: "Ventas Inventario",
  abonos_apartados: "Abonos Apartados",
  compras_pagadas: "Compras / Gastos Pagados",
};

export default function TribuCRDialog({ open, onClose, boxes, defaultBoxId, isMaster }) {
  const now = new Date();
  const [boxId, setBoxId] = useState(defaultBoxId || "");
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [quarter, setQuarter] = useState(String(Math.ceil((now.getMonth() + 1) / 3)));
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  // Box config (regime + factors + actividad)
  const [savingConfig, setSavingConfig] = useState(false);
  const [boxRegime, setBoxRegime] = useState("tradicional");
  const [boxFactor, setBoxFactor] = useState("2");
  const [boxFactorIva, setBoxFactorIva] = useState("");
  const [boxFactorRenta, setBoxFactorRenta] = useState("");
  const [boxActivityCode, setBoxActivityCode] = useState("");
  const [activities, setActivities] = useState([]);

  // Renta anual (tradicional)
  const [rentaReport, setRentaReport] = useState(null);
  const [rentaLoading, setRentaLoading] = useState(false);
  const [rentaTipo, setRentaTipo] = useState("juridica_pequena");

  useEffect(() => {
    if (open) {
      setBoxId(defaultBoxId || "");
      setReport(null);
      setRentaReport(null);
      // Load official activities table
      axiosInstance.get("/tributary/activities").then((r) => setActivities(r.data.activities || [])).catch(() => setActivities([]));
    }
  }, [open, defaultBoxId]);

  // Refresh regime + factors when box changes
  useEffect(() => {
    if (!boxId) return;
    const b = boxes.find((x) => x.id === boxId);
    if (b) {
      setBoxRegime(b.tributary_regime || "tradicional");
      setBoxFactor(String(b.tributary_factor || 2));
      setBoxFactorIva(b.tributary_factor_iva !== undefined && b.tributary_factor_iva !== null ? String(b.tributary_factor_iva) : "");
      setBoxFactorRenta(b.tributary_factor_renta !== undefined && b.tributary_factor_renta !== null ? String(b.tributary_factor_renta) : "");
      setBoxActivityCode(b.tributary_activity_code || "");
    }
  }, [boxId, boxes]);

  const handleSelectActivity = (code) => {
    setBoxActivityCode(code);
    const a = activities.find((x) => x.code === code);
    if (a) {
      setBoxFactorIva(String(a.factor_iva));
      setBoxFactorRenta(String(a.factor_renta));
    }
  };

  const handleSaveConfig = async () => {
    if (!boxId) {
      toast.error("Selecciona una caja");
      return;
    }
    setSavingConfig(true);
    try {
      const params = new URLSearchParams();
      params.set("regime", boxRegime);
      params.set("factor", boxFactor);
      if (boxFactorIva !== "") params.set("factor_iva", boxFactorIva);
      if (boxFactorRenta !== "") params.set("factor_renta", boxFactorRenta);
      if (boxActivityCode) {
        params.set("activity_code", boxActivityCode);
        const a = activities.find((x) => x.code === boxActivityCode);
        if (a) params.set("activity_name", a.name);
      }
      await axiosInstance.patch(`/boxes/${boxId}/tributary?${params.toString()}`);
      toast.success("Régimen guardado");
      const b = boxes.find((x) => x.id === boxId);
      if (b) {
        b.tributary_regime = boxRegime;
        b.tributary_factor = parseFloat(boxFactor);
        if (boxFactorIva !== "") b.tributary_factor_iva = parseFloat(boxFactorIva);
        if (boxFactorRenta !== "") b.tributary_factor_renta = parseFloat(boxFactorRenta);
        if (boxActivityCode) b.tributary_activity_code = boxActivityCode;
      }
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al guardar régimen"));
    } finally {
      setSavingConfig(false);
    }
  };

  const handleFetchRentaAnual = async () => {
    if (!boxId) { toast.error("Selecciona una caja"); return; }
    setRentaLoading(true);
    setRentaReport(null);
    try {
      const res = await axiosInstance.get(`/reports/tribucr-renta-anual?box_id=${boxId}&year=${year}&tipo=${rentaTipo}`);
      setRentaReport(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al calcular renta anual"));
    } finally {
      setRentaLoading(false);
    }
  };

  const handleFetch = async () => {
    if (!boxId) {
      toast.error("Selecciona una caja");
      return;
    }
    setLoading(true);
    setReport(null);
    try {
      let url = `/reports/tribucr?box_id=${boxId}&year=${year}`;
      if (boxRegime === "simplificado") {
        url += `&quarter=${quarter}`;
      } else {
        url += `&month=${month}`;
      }
      const res = await axiosInstance.get(url);
      setReport(res.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al generar reporte"));
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = (b64, filename, mime) => {
    const byteCharacters = atob(b64);
    const arr = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) arr[i] = byteCharacters.charCodeAt(i);
    const blob = new Blob([arr], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (kind) => {
    if (!boxId) {
      toast.error("Selecciona una caja");
      return;
    }
    setLoading(true);
    try {
      let url = `/reports/tribucr-${kind}?box_id=${boxId}&year=${year}`;
      if (boxRegime === "simplificado") {
        url += `&quarter=${quarter}`;
      } else {
        url += `&month=${month}`;
      }
      const res = await axiosInstance.get(url);
      if (kind === "pdf") {
        downloadFile(res.data.pdf_base64, res.data.filename, "application/pdf");
      } else {
        downloadFile(
          res.data.excel_base64,
          res.data.filename,
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        );
      }
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al descargar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="tribucr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" /> Reporte TribuCR - Hacienda Costa Rica
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Box selector + Config */}
          <Card className="bg-slate-50">
            <CardContent className="py-3 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Caja</label>
                  <Select value={boxId} onValueChange={setBoxId}>
                    <SelectTrigger data-testid="tribucr-box-select"><SelectValue placeholder="Seleccionar caja" /></SelectTrigger>
                    <SelectContent>
                      {boxes.map((b) => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isMaster && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-slate-600">Régimen</label>
                      <Select value={boxRegime} onValueChange={setBoxRegime}>
                        <SelectTrigger data-testid="tribucr-regime-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tradicional">Tradicional</SelectItem>
                          <SelectItem value="simplificado">Simplificado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button size="sm" onClick={handleSaveConfig} disabled={savingConfig || !boxId} data-testid="tribucr-save-config-btn">
                      {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
                    </Button>
                  </div>
                )}
              </div>

              {isMaster && boxRegime === "simplificado" && (
                <div className="space-y-2 border-t pt-2">
                  <label className="text-xs font-semibold text-slate-700">
                    Actividad económica (Hacienda CR) — al elegirla se rellenan los factores oficiales
                  </label>
                  <Select value={boxActivityCode} onValueChange={handleSelectActivity}>
                    <SelectTrigger data-testid="tribucr-activity-select">
                      <SelectValue placeholder="Seleccionar actividad económica" />
                    </SelectTrigger>
                    <SelectContent className="max-h-96">
                      {activities.map((a) => (
                        <SelectItem key={a.code} value={a.code}>
                          {(a.factor_renta * 100).toFixed(2)}% Renta / {(a.factor_iva * 100).toFixed(3)}% IVA — {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600">Factor Renta (decimal)</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={boxFactorRenta}
                        onChange={(e) => setBoxFactorRenta(e.target.value)}
                        placeholder="0.01 = 1%"
                        className="w-full px-2 py-1 border rounded text-sm"
                        data-testid="tribucr-factor-renta"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600">Factor IVA (decimal)</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={boxFactorIva}
                        onChange={(e) => setBoxFactorIva(e.target.value)}
                        placeholder="0.02 = 2%"
                        className="w-full px-2 py-1 border rounded text-sm"
                        data-testid="tribucr-factor-iva"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 italic">
                    Total trimestral a pagar = Compras × ({boxFactorRenta ? (parseFloat(boxFactorRenta) * 100).toFixed(2) : "?"}% + {boxFactorIva ? (parseFloat(boxFactorIva) * 100).toFixed(3) : "?"}%). Vencimientos: 15 abr / 15 jul / 15 oct / 15 ene.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-600">Año</label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {boxRegime === "simplificado" ? (
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-slate-600">Trimestre</label>
                    <Select value={quarter} onValueChange={setQuarter}>
                      <SelectTrigger data-testid="tribucr-quarter-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {QUARTERS.map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="col-span-2">
                    <label className="text-xs font-medium text-slate-600">Mes</label>
                    <Select value={month} onValueChange={setMonth}>
                      <SelectTrigger data-testid="tribucr-month-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button onClick={handleFetch} disabled={loading || !boxId} className="bg-blue-700 hover:bg-blue-800" data-testid="tribucr-generate-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Generar Reporte
                </Button>
                {report && (
                  <>
                    <Button variant="outline" onClick={() => handleDownload("pdf")} disabled={loading} data-testid="tribucr-pdf-btn">
                      <FileText className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button variant="outline" onClick={() => handleDownload("excel")} disabled={loading} data-testid="tribucr-excel-btn">
                      <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Report Content */}
          {report && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <h3 className="font-bold text-blue-900">{report.box_name} — {report.period_label}</h3>
                <p className="text-xs text-blue-700">
                  Régimen <b>{report.regime.toUpperCase()}</b>
                  {report.regime === "simplificado" ? (
                    <> · Factores: <b>{report.factor_renta_percent}%</b> Renta / <b>{report.factor_iva_percent}%</b> IVA {report.activity_name && <>· <b>{report.activity_name}</b></>}</>
                  ) : (
                    <> · IVA 13%</>
                  )}
                  {" · "}Del {report.date_from} al {report.date_to} · {report.total_transactions} transacciones en {report.total_registers} cajas
                </p>
              </div>

              {/* Summary */}
              <Card>
                <CardContent className="py-3">
                  <h4 className="font-bold text-sm mb-2 text-slate-700">Resumen Tributario</h4>
                  <table className="w-full text-sm border-collapse">
                    <tbody>
                      <tr className="border-b">
                        <td className="p-1.5 text-slate-600">Ingresos Brutos</td>
                        <td className="p-1.5 text-right font-mono">{fmtCRC(report.ingresos_brutos_colones)}</td>
                        <td className="p-1.5 text-right font-mono text-slate-500">{fmtUSD(report.ingresos_brutos_dollars)}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-1.5 text-slate-600">Premios Pagados (Salida)</td>
                        <td className="p-1.5 text-right font-mono">{fmtCRC(report.premios_colones)}</td>
                        <td></td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-1.5 text-slate-600">Compras / Gastos Pagados</td>
                        <td className="p-1.5 text-right font-mono">{fmtCRC(report.compras_colones)}</td>
                        <td className="p-1.5 text-right font-mono text-slate-500">{fmtUSD(report.compras_dollars)}</td>
                      </tr>
                      {report.regime === "tradicional" && (
                        <>
                          <tr className="border-b">
                            <td className="p-1.5 text-slate-600">IVA Débito 13% (Ventas)</td>
                            <td className="p-1.5 text-right font-mono">{fmtCRC(report.iva_debito_colones)}</td>
                            <td></td>
                          </tr>
                          <tr className="border-b">
                            <td className="p-1.5 text-slate-600">IVA Crédito 13% (Compras)</td>
                            <td className="p-1.5 text-right font-mono">{fmtCRC(report.iva_credito_colones)}</td>
                            <td></td>
                          </tr>
                        </>
                      )}
                      <tr className="bg-amber-100 font-bold">
                        <td className="p-2">IVA a Pagar</td>
                        <td className="p-2 text-right font-mono">{fmtCRC(report.iva_pagar_colones)}</td>
                        <td className="p-2 text-right font-mono">{fmtUSD(report.iva_pagar_dollars)}</td>
                      </tr>
                      {report.regime === "simplificado" && (
                        <>
                          <tr className="bg-orange-100 font-bold">
                            <td className="p-2">Renta a Pagar</td>
                            <td className="p-2 text-right font-mono">{fmtCRC(report.renta_pagar_colones)}</td>
                            <td className="p-2 text-right font-mono">{fmtUSD(report.renta_pagar_dollars)}</td>
                          </tr>
                          <tr className="bg-emerald-200 font-bold text-lg">
                            <td className="p-2">💰 TOTAL A PAGAR (D-105-2)</td>
                            <td className="p-2 text-right font-mono">{fmtCRC(report.total_pagar_colones)}</td>
                            <td className="p-2 text-right font-mono">{fmtUSD(report.total_pagar_dollars)}</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Categories */}
              <Card>
                <CardContent className="py-3">
                  <h4 className="font-bold text-sm mb-2 text-slate-700">Movimientos por Categoría</h4>
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-slate-100">
                        <th className="p-1.5 text-left">Categoría</th>
                        <th className="p-1.5 text-right">Cantidad</th>
                        <th className="p-1.5 text-right">Total ₡</th>
                        <th className="p-1.5 text-right">Total $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(CAT_LABELS).map(([key, lbl]) => {
                        const c = report.by_category[key] || {};
                        return (
                          <tr key={key} className="border-b">
                            <td className="p-1.5">{lbl}</td>
                            <td className="p-1.5 text-right">{c.count || 0}</td>
                            <td className="p-1.5 text-right font-mono">{fmtCRC(c.amount_c)}</td>
                            <td className="p-1.5 text-right font-mono text-slate-500">{fmtUSD(c.amount_d)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Expense detail */}
              {report.expenses_detail.length > 0 && (
                <Card>
                  <CardContent className="py-3">
                    <h4 className="font-bold text-sm mb-2 text-slate-700">Detalle de Compras / Gastos ({report.expenses_detail.length})</h4>
                    <div className="overflow-x-auto max-h-64 overflow-y-auto">
                      <table className="w-full text-xs border-collapse">
                        <thead className="bg-amber-100 sticky top-0">
                          <tr>
                            <th className="p-1.5 text-left">Fecha</th>
                            <th className="p-1.5 text-left">Beneficiario</th>
                            <th className="p-1.5 text-left">Descripción</th>
                            <th className="p-1.5 text-right">₡</th>
                            <th className="p-1.5 text-right">$</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.expenses_detail.map((e, i) => (
                            <tr key={i} className="border-b">
                              <td className="p-1">{e.paid_at}</td>
                              <td className="p-1">{e.recipient}</td>
                              <td className="p-1 text-slate-600">{e.description}</td>
                              <td className="p-1 text-right font-mono">{fmtCRC(e.amount_colones)}</td>
                              <td className="p-1 text-right font-mono">{fmtUSD(e.amount_dollars)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Renta Anual (Tradicional) */}
          {boxRegime === "tradicional" && boxId && (
            <Card className="border-emerald-300">
              <CardContent className="py-3 space-y-3">
                <h4 className="font-bold text-sm text-emerald-800 flex items-center gap-2">
                  🧾 Impuesto sobre la Renta Anual (proyección)
                </h4>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-slate-600">Tipo de contribuyente</label>
                    <Select value={rentaTipo} onValueChange={setRentaTipo}>
                      <SelectTrigger data-testid="tribucr-renta-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="juridica_pequena">Persona jurídica pequeña (ingresos &lt; ₡122.145M)</SelectItem>
                        <SelectItem value="fisica">Persona física con actividad lucrativa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleFetchRentaAnual} disabled={rentaLoading} className="bg-emerald-700 hover:bg-emerald-800" data-testid="tribucr-renta-btn">
                    {rentaLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Calcular Renta Año {year}
                  </Button>
                </div>
                {rentaReport && (
                  <div className="border rounded p-2 bg-slate-50 text-sm">
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <span className="text-slate-600">(+) Ingresos totales:</span>
                      <span className="text-right font-mono">{fmtCRC(rentaReport.ingresos_colones)}</span>
                      <span className="text-slate-600">(-) Costos (compras a proveedores):</span>
                      <span className="text-right font-mono text-red-700">{fmtCRC(rentaReport.costos_colones)}</span>
                      <span className="text-slate-600">(-) Gastos deducibles:</span>
                      <span className="text-right font-mono text-red-700">{fmtCRC(rentaReport.gastos_deducibles_colones)}</span>
                      {rentaReport.hr_neto_colones > 0 && (
                        <>
                          <span className="text-slate-500 pl-3 text-[11px]">↳ Planillas HR (netos)</span>
                          <span className="text-right font-mono text-red-600 text-[11px]">{fmtCRC(rentaReport.hr_neto_colones)}</span>
                        </>
                      )}
                      {Object.entries(rentaReport.gastos_detalle || {}).filter(([k]) => k !== "Planillas (netos pagados)").slice(0, 6).map(([k, v]) => (
                        <div key={k} className="contents">
                          <span className="text-slate-500 pl-3 text-[11px]">↳ {k}</span>
                          <span className="text-right font-mono text-red-600 text-[11px]">{fmtCRC(v)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-2 pt-2 grid grid-cols-2 gap-1 font-bold">
                      <span>Utilidad Neta:</span>
                      <span className={"text-right font-mono " + (rentaReport.utilidad_neta_colones >= 0 ? "text-emerald-700" : "text-red-700")}>
                        {fmtCRC(rentaReport.utilidad_neta_colones)}
                      </span>
                      <span className="text-emerald-900">🧾 Impuesto Renta estimado:</span>
                      <span className="text-right font-mono text-emerald-900">{fmtCRC(rentaReport.impuesto_renta_colones)}</span>
                    </div>
                    {rentaReport.tramos_aplicados.length > 0 && (
                      <div className="mt-2 pt-2 border-t">
                        <p className="text-xs font-semibold text-slate-700">Tramos aplicados:</p>
                        {rentaReport.tramos_aplicados.map((t, i) => (
                          <div key={i} className="text-[11px] text-slate-600 flex justify-between">
                            <span>{fmtCRC(t.desde)} → {t.hasta ? fmtCRC(t.hasta) : "en adelante"} @ {(t.tasa * 100).toFixed(0)}%</span>
                            <span className="font-mono">{fmtCRC(t.impuesto)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <p className="text-[10px] text-slate-500 italic mt-2">
                      Vencimiento anual: primeros 15 días de marzo del año siguiente (formulario D-101). Esta es una proyección — verificar con contador.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
